import { Progress } from 'antd';
import PropType from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_TYPE, UNIT_TYPE } from 'util/config/constant';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';

export const UtilisationCleaningGraph = ({
  totalValue,
  item,
  unit,
  time,
  isLoading
}) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  const {
    name,
    currentStatistics,
    startDateOfWeek,
    endDateOfWeek,
    isoYear,
    isoWeek,
    date
  } = item || {};

  const [activeNumber, setActiveNumber] = useState(false);

  const crStatistics = useMemo(() => {
    return {
      hours: currentStatistics.hours,
      cleanedSquareMeter: currentStatistics.cleanedSquareMeter
    };
  }, [currentStatistics]);

  const currentValue = useMemo(() => {
    return unit === UNIT_TYPE.hours
      ? crStatistics.hours
      : crStatistics.cleanedSquareMeter;
  }, [crStatistics.cleanedSquareMeter, crStatistics.hours, unit]);

  const onHover = useCallback((activeValue) => {
    setActiveNumber(activeValue);
  }, []);

  const renderChartItemName = useCallback(() => {
    switch (time) {
      case TIME_TYPE.Past6Months:
        return (
          <span>
            {t(`cleaningAreaPanel.sixMonthsDateMapping.${name}`, {
              year: isoYear.toString().substr(-2)
            })}
          </span>
        );
      case TIME_TYPE.Month:
        return (
          <>
            <span>
              {t('cleaningAreaPanel.numOfWeek', {
                value: isoWeek
              })}
            </span>
            {startDateOfWeek && endDateOfWeek && (
              <span className="date">
                {startDateOfWeek} - {endDateOfWeek}
              </span>
            )}
          </>
        );
      case TIME_TYPE.Week:
        return (
          <span>
            {t(`cleaningAreaPanel.thisWeekDateMapping.${name}`, {
              day: date
            })}
          </span>
        );
      default:
        return (
          <span>
            {t(`cleaningAreaPanel.thisYearDateMapping.${name}`, {
              year: isoYear.toString().substr(-2)
            })}
          </span>
        );
    }
  }, [date, endDateOfWeek, isoWeek, isoYear, name, startDateOfWeek, t, time]);

  const renderChart = useMemo(() => {
    if (isLoading) return <></>;

    return (
      <div
        className="utilisation-cleaning-graph"
        data-testid={name}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}>
        <div className="progress-wrapper">
          <div className={`number ${activeNumber ? 'active-number' : ''}`}>
            {currentValue}
          </div>
          <Progress
            className="progress"
            showInfo={false}
            percent={(currentValue / totalValue) * 100}
          />
        </div>
        <div className="name">{renderChartItemName()}</div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return <>{renderChart}</>;
};

UtilisationCleaningGraph.propTypes = {
  totalValue: PropType.number.isRequired,
  item: PropType.shape().isRequired,
  unit: PropType.string.isRequired,
  time: PropType.string.isRequired,
  isLoading: PropType.bool.isRequired
};
